import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'

interface Aehnliche {
  calculationId: string
  calculationLabel: string
  submittedAt: string | null
  positionId: string
  label: string
  shortText: string
  unit: string | null
  quantity: number
  similarity: number
  hoursPerUnit: number | null
  unitPrice: number | null
  steps: { activityName: string; hoursPerUnit: number }[]
  costLines: { kind: string; name: string; unitCode: string | null; quantityPerUnit: number }[]
  actual: { hoursPerUnit: number; projectPositions: number } | null
}

const zahl = (x: number | null | undefined, stellen = 3) => (x === null || x === undefined ? '—' : x.toLocaleString('de-DE', { maximumFractionDigits: stellen }))

/**
 * AP-37 Detailblatt N 6.5 (N4b) — **Erfahrung**: ähnliche Positionen abgegebener Kalkulationen
 * mit Stunden je Einheit, EP und — mit Nachkalkulation — Ist. Nur eigene Daten, ohne Kunden.
 */
export function ErfahrungDialog({ kalkulationId, positionId, onClose }: { kalkulationId: string; positionId: string; onClose: () => void }) {
  const [daten, setDaten] = useState<{ data: Aehnliche[]; meta?: { controlling: boolean } } | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    api
      .get<{ data: Aehnliche[]; meta?: { controlling: boolean } }>(`/calculations/${kalkulationId}/positions/${positionId}/experience`)
      .then((r) => setDaten(r.data))
      .catch((e) => setFehler(apiErrorText(e, 'Die Erfahrungswerte ließen sich nicht laden.')))
  }, [kalkulationId, positionId])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div
        className="my-8 w-full max-w-3xl space-y-3 rounded-lg border border-border p-4 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Erfahrung — ähnliche Positionen</h2>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Aus abgegebenen Kalkulationen, gleiche Einheitengruppe, Ähnlichkeit der Bezeichnung ab 35 %.
          {daten?.meta?.controlling ? ' Ist aus der Nachkalkulation abgeschlossener Positionen.' : ''}
        </p>
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        {!daten && !fehler && <p className="text-sm text-muted-foreground">Sucht …</p>}
        {daten && !daten.data.length && <p className="py-4 text-center text-sm text-muted-foreground">Keine ähnliche Position gefunden.</p>}
        {daten && daten.data.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-1 pr-2">Kalkulation</th>
                <th className="py-1 pr-2">Position</th>
                <th className="py-1 pr-2 text-right">ähnlich</th>
                <th className="py-1 pr-2 text-right">h/E</th>
                <th className="py-1 pr-2 text-right">Ist h/E</th>
                <th className="py-1 text-right">EP</th>
              </tr>
            </thead>
            <tbody>
              {daten.data.map((a) => (
                <Fragment key={a.positionId}>
                  <tr className="cursor-pointer border-b border-border/60 hover:bg-muted/40" onClick={() => setOffen(offen === a.positionId ? null : a.positionId)}>
                    <td className="py-1 pr-2">
                      <Link className="text-primary hover:underline" to={`/calculations/${a.calculationId}?tab=positionen&pos=${a.positionId}`} onClick={(e) => e.stopPropagation()}>
                        {a.calculationLabel}
                      </Link>
                    </td>
                    <td className="py-1 pr-2">
                      {a.label} {a.shortText} <span className="text-xs text-muted-foreground">({zahl(a.quantity)} {a.unit ?? ''})</span>
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{Math.round(a.similarity * 100)} %</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{zahl(a.hoursPerUnit)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{a.actual ? `${zahl(a.actual.hoursPerUnit)} (${a.actual.projectPositions})` : '—'}</td>
                    <td className="py-1 text-right tabular-nums">{a.unitPrice === null ? '—' : geldText(a.unitPrice)}</td>
                  </tr>
                  {offen === a.positionId && (
                    <tr className="border-b border-border/60 bg-muted/20 text-xs">
                      <td />
                      <td colSpan={5} className="py-1.5 pr-2">
                        {a.steps.map((s, i) => (
                          <div key={`s${i}`}>
                            {s.activityName}: {zahl(s.hoursPerUnit, 4)} h/E
                          </div>
                        ))}
                        {a.costLines.map((z, i) => (
                          <div key={`z${i}`} className="text-muted-foreground">
                            {z.name}: {zahl(z.quantityPerUnit, 4)} {z.unitCode ?? ''}/E
                          </div>
                        ))}
                        {!a.steps.length && !a.costLines.length && <div className="text-muted-foreground">Blatt leer.</div>}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
